import { createContext, useContext, useState, useEffect, useRef, type ReactNode } from 'react';
import { getAuth, signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import type { User as FirebaseUser } from 'firebase/auth';
import { initializeApp } from 'firebase/app';
import { api, ApiError } from '../services/api';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// Vite bakes these in at build time, so a missing value means the build was made
// without the right env file – fail loudly instead of hitting a cryptic Firebase error.
const missingConfig = Object.entries(firebaseConfig)
  .filter(([key, value]) => key !== 'measurementId' && !value)
  .map(([key]) => key);

if (missingConfig.length > 0) {
  throw new Error(
    `Missing Firebase config: ${missingConfig.join(', ')}. See .env.example.`
  );
}

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

/**
 * 'admin' = full admin (users/{uid}.is_admin == True on the backend).
 * 'contributor' = approved Blog/Press contributor only.
 * 'none' = signed in with Firebase but neither of the above (shouldn't
 * normally reach the admin panel, but the backend is what actually
 * prevents them from doing anything if they do).
 */
export type AdminRole = 'admin' | 'contributor' | 'none';

interface AuthContextType {
  user: FirebaseUser | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /**
   * Resolved once per sign-in — see resolveRole() below. This drives what
   * the UI shows/hides only; it is NOT itself an authorization check. The
   * backend's own admin_required / blog_press_manage_required decorators
   * are the real gate on every API call, exactly as before this role
   * concept existed.
   */
  role: AdminRole | null;
  roleLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * There is no backend endpoint (yet) that returns the signed-in user's own
 * is_admin / is_blog_press_contributor flags - this repo and the backend
 * were both inspected for one ("/me", "/whoami", etc.) and neither exists.
 * Adding one is a backend change, out of scope for this phase (see the
 * handover notes). Until that exists, this infers the role by attempting
 * two calls the account can only succeed at if the backend's own decorator
 * allows it:
 *   - GET /api/admin/blog-press-access-requests (admin_required only, see
 *     backend/app/api/admin/blog_press_access.py) -> success means full admin.
 *   - GET /api/admin/blog-press (blog_press_manage_required: is_admin OR
 *     is_blog_press_contributor, see backend/app/api/admin/content.py) ->
 *     success means at least a contributor.
 * Any other outcome is treated as 'none'. This is a client-side stopgap
 * for UI purposes only, not a security boundary — the probed endpoints'
 * own backend checks are what's actually enforcing anything.
 */
async function resolveRole(): Promise<AdminRole> {
  try {
    await api.getBlogPressAccessRequests({ limit: 1 });
    return 'admin';
  } catch {
    // Not a full admin (403) - or the probe itself failed for an unrelated
    // reason, in which case the contributor probe below will also fail and
    // correctly fall through to 'none'.
  }

  try {
    await api.getBlogPressArticles();
    return 'contributor';
  } catch {
    return 'none';
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [role, setRole] = useState<AdminRole | null>(null);
  const [roleLoading, setRoleLoading] = useState(false);
  // Guards the role-resolution effect below so it runs exactly once per
  // signed-in uid, not on every re-render and not again on token refresh.
  const resolvedForUid = useRef<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Check if user is admin
        try {
          const token = await firebaseUser.getIdToken();
          localStorage.setItem('admin_token', token);
          setUser(firebaseUser);
        } catch (err) {
          console.error('Error checking admin status:', err);
          setUser(null);
        }
      } else {
        localStorage.removeItem('admin_token');
        setUser(null);
        setRole(null);
        resolvedForUid.current = null;
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user || resolvedForUid.current === user.uid) return;
    resolvedForUid.current = user.uid;

    void (async () => {
      setRoleLoading(true);
      // Best-effort, once per sign-in: link this account to an approved
      // Blog/Press request for the same verified email, if one exists. A
      // 404 ("no approved request for this email") is the expected, silent
      // outcome for every full admin and every contributor who was already
      // linked at approval time - it is not an error.
      try {
        await api.claimBlogPressContributorAccess();
      } catch (err) {
        const status = err instanceof ApiError ? err.status : null;
        if (status !== 404 && status !== 409) {
          console.warn('Blog/Press contributor claim check failed:', err);
        }
      }

      const resolved = await resolveRole();
      setRole(resolved);
      setRoleLoading(false);
    })();
  }, [user]);

  const login = async (email: string, password: string) => {
    setError(null);
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      const token = await result.user.getIdToken();
      localStorage.setItem('admin_token', token);
      setUser(result.user);
    } catch (err: any) {
      setError(err.message || 'Failed to login');
      throw err;
    }
  };

  const logout = async () => {
    await signOut(auth);
    localStorage.removeItem('admin_token');
    setUser(null);
    setRole(null);
    resolvedForUid.current = null;
  };

  return (
    <AuthContext.Provider value={{ user, loading, error, login, logout, role, roleLoading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export { auth };
